import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  createFilterOptions,
  useTheme,
} from '@mui/material'
import Cookies from 'js-cookie'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useGetCaseDetailsQuery,
  useGetRecipesQuery,
  useUpdateCaseDetailMutation,
} from '@/store/apiService'
import { getCaseId } from '@/utils'

import Button from './Button'
import Dialog from './Dialog'

type SaveRecipeDialogProps = {
  saveRecipedialogOpen: boolean
  setSaveRecipedialogOpen: (open: boolean) => void
  recipeName: string
  setRecipeName: (name: string) => void
  onSave: (targetCaseId?: string) => void
  isLoading: boolean
}

function SaveRecipeDialog({
  saveRecipedialogOpen,
  setSaveRecipedialogOpen,
  recipeName,
  setRecipeName,
  onSave,
  isLoading,
}: SaveRecipeDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'saveRecipeDialog',
  })
  const isFormValid = !!recipeName.trim()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecipeName(e.target.value)
  }

  const caseId = getCaseId() || ''

  const { data: caseDetails, refetch: refetchCaseDetails } =
    useGetCaseDetailsQuery({ caseId }, { skip: !caseId })

  const { data: recipeData } = useGetRecipesQuery(
    { shared: false, paginated: false },
    { skip: !saveRecipedialogOpen },
  )

  const [updateCaseDetail] = useUpdateCaseDetailMutation()

  const [selectedProject, setSelectedProject] = useState<any>(null)

  const handleProjectChange = (_event: React.SyntheticEvent, newValue: any) => {
    if (typeof newValue === 'string') {
      setSelectedProject({ id: null, title: newValue, isNew: true })
    } else if (newValue?.inputValue) {
      setSelectedProject({ id: null, title: newValue.inputValue, isNew: true })
    } else {
      setSelectedProject(newValue)
    }
  }

  const theme = useTheme()

  const handleSave = async () => {
    const isAutoCase = caseDetails?.case_type === 'auto'
    const targetCaseId = isAutoCase ? selectedProject?.id : caseId

    if (isAutoCase && selectedProject?.isNew && selectedProject?.title) {
      try {
        await updateCaseDetail({
          caseId: caseId,
          name: selectedProject.title,
          case_type: 'manual',
        }).unwrap()

        Cookies.set('case_id', caseId)
        refetchCaseDetails()
        onSave(caseId)
      } catch (error) {
        console.error('Failed to update case details:', error)
      }
    } else {
      Cookies.set('case_id', targetCaseId)
      onSave(targetCaseId)
    }
  }

  const filter = createFilterOptions()

  return (
    <Dialog
      isOpen={saveRecipedialogOpen}
      onClose={() => setSaveRecipedialogOpen(false)}
      title={
        <Typography fontSize={24} fontWeight={500}>
          {t('title')}
        </Typography>
      }
      content={
        <Box display="flex" flexDirection="column" gap={2}>
          {caseDetails?.case_type === 'auto' && (
            <Autocomplete
              value={selectedProject}
              onChange={handleProjectChange}
              options={
                Array.isArray(recipeData)
                  ? recipeData.map((item) => ({
                      ...item,
                      title: item.name || 'Untitled',
                      disabled: item.id === caseId,
                    }))
                  : []
              }
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option
                if (option.inputValue) return option.inputValue
                return option.title || ''
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params)
                const isExisting = options.some(
                  (option) =>
                    option.title?.toLowerCase?.() ===
                    params.inputValue.trim().toLowerCase(),
                )

                if (params.inputValue !== '' && !isExisting) {
                  filtered.push({
                    inputValue: params.inputValue,
                    title: `Add "${params.inputValue}"`,
                    id: `custom-${params.inputValue}`,
                    isNew: true,
                  })
                }

                return filtered
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={`${option.id}-${option.title}`}
                  style={{
                    opacity: option.disabled ? 0.5 : 1,
                    pointerEvents: option.disabled ? 'none' : 'auto',
                  }}
                >
                  <Box>
                    <Typography fontWeight={400} fontSize={'16px'}>
                      {option.title}
                    </Typography>
                  </Box>
                </li>
              )}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('placeholder.createAproject')}
                  size="medium"
                  label={t('projectName')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      '& input': {
                        '&:-webkit-autofill': {
                          boxShadow: '0 0 0 1000px #292929 inset',
                          transition: 'background-color 5000s ease-in-out 0s',
                        },
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#5B5B5B',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#90CAF9',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      top: '-8px',
                    },
                    '& .MuiInputLabel-shrink': {
                      top: '0px',
                    },
                  }}
                />
              )}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: { boundary: 'window' },
                    },
                  ],
                },
                paper: {
                  sx: {
                    backgroundColor: theme.palette.background.disableTab,
                    fontSize: 16,
                    fontWeight: 400,
                    paddingY: '6px',
                  },
                },
              }}
            />
          )}

          <TextField
            label={t('recipeName')}
            name="recipeName"
            value={recipeName}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                '& input': {
                  '&:-webkit-autofill': {
                    boxShadow: '0 0 0 1000px #292929 inset',
                    transition: 'background-color 5000s ease-in-out 0s',
                  },
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#5B5B5B',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#90CAF9',
                },
              },
              '& .MuiInputLabel-root': {
                top: '-8px',
              },
              '& .MuiInputLabel-shrink': {
                top: '0px',
              },
            }}
          />
        </Box>
      }
      actionButtons={
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            label={t('cancelButton')}
            onClick={() => setSaveRecipedialogOpen(false)}
            variant="outlined"
            textTransform="uppercase"
          />
          <Button
            label={t('saveButton')}
            onClick={handleSave}
            variant="contained"
            disabled={!isFormValid || isLoading}
            loading={isLoading}
            textTransform="uppercase"
          />
        </Box>
      }
      paperStyles={{ width: '800px' }}
      triggerElement={null}
    />
  )
}

export default SaveRecipeDialog
